import { ChevronRight } from "@cynthai/icons";
import { Button } from "@cynthai/ui";
import { useState } from "react";
import { Dialog, DialogTrigger, Popover } from "react-aria-components";
import { twMerge } from "tailwind-merge";

import { canManageHelpCenter } from "@/core/lib/access";
import { UserAvatar } from "@/core/ui/UserAvatar";
import type { UserRead } from "@/openapi/types.gen";

import { SidebarProfileMenu } from "./SidebarProfileMenu";
import { sidebarContentStyles, sidebarItemStyles } from "./styles";

type SidebarProfileProps = {
  currentUser: UserRead;
  isExpanded: boolean;
  onNavigationMenuOpenChange: (isOpen: boolean) => void;
  onCloseNavigationMenus: () => void;
};

export function SidebarProfile({
  currentUser,
  isExpanded,
  onNavigationMenuOpenChange,
  onCloseNavigationMenus,
}: Readonly<SidebarProfileProps>) {
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const {
    label: footerLabelClassName,
    chevronWrapper: footerChevronClassName,
  } = sidebarContentStyles({
    expanded: isExpanded,
  });
  const handleProfileMenuOpenChange = (isOpen: boolean) => {
    setIsProfileMenuOpen(isOpen);

    if (isOpen) {
      onNavigationMenuOpenChange(true);
      return;
    }

    onCloseNavigationMenus();
  };
  const closeNavigationMenus = () => {
    setIsProfileMenuOpen(false);
    onCloseNavigationMenus();
  };

  return (
    <div className="mt-4 shrink-0">
      <DialogTrigger
        isOpen={isProfileMenuOpen}
        onOpenChange={handleProfileMenuOpenChange}
      >
        <Button
          aria-label={currentUser.full_name}
          className={twMerge(
            "!py-8",
            sidebarItemStyles({
              expanded: isExpanded,
              className: "px-0",
            }),
          )}
        >
          <UserAvatar
            user={currentUser}
            size="md"
            className="border-border size-12 border"
          />
          <span
            aria-hidden={!isExpanded}
            className={twMerge(footerLabelClassName(), "text-left font-medium")}
          >
            {currentUser.full_name}
          </span>
          <span
            aria-hidden={!isExpanded}
            className={twMerge(footerChevronClassName(), "ml-auto")}
          >
            <ChevronRight width={20} height={20} />
          </span>
        </Button>

        <Popover
          placement="right"
          offset={30}
          className="border-border bg-background-2 w-70 rounded-lg border p-3 shadow-xl outline-none"
        >
          <Dialog aria-label={currentUser.full_name}>
            {({ close }) => (
              <SidebarProfileMenu
                canManageHelpCenter={canManageHelpCenter(currentUser)}
                organizationId={currentUser.organization?.id}
                userId={currentUser.id}
                closeProfileMenu={() => {
                  close();
                  closeNavigationMenus();
                }}
              />
            )}
          </Dialog>
        </Popover>
      </DialogTrigger>
    </div>
  );
}
